import { type AnalyticsEvent, trackAnalyticsEvent } from '@autone/analytics';
import { useAutoneTranslation } from '@autone/translations';
import type { Portal as RadixTooltipPortal } from '@radix-ui/react-tooltip';
// @ts-ignore
import { Copy } from 'lucide-react';
import { useEffect, useState } from 'react';

import { type APP_NAME } from '../../../../apps/reorder/src/constant';

import { Button, type ButtonProps } from './Button';
import {
  TailwindTooltip as Tooltip,
  TailwindTooltipContent as TooltipContent,
  TailwindTooltipProvider as TooltipProvider,
  TailwindTooltipTrigger as TooltipTrigger,
} from './Tooltip';
import { cn } from './utils';

async function copyToClipboardWithMeta(
  value: string,
  analyticsEvent?: AnalyticsEvent,
) {
  navigator.clipboard.writeText(value);
  if (analyticsEvent) trackAnalyticsEvent({ ...analyticsEvent });
}

interface CopyButtonProps extends ButtonProps {
  value: string;
  analyticsEvent?: AnalyticsEvent;
  container?: React.ComponentPropsWithoutRef<
    typeof RadixTooltipPortal
  >['container'];
}

function CopyButton({
  value,
  className,
  variant = 'ghost',
  analyticsEvent,
  container,
  ...props
}: CopyButtonProps) {
  const { common } = useAutoneTranslation<typeof APP_NAME>();
  const [hasCopied, setHasCopied] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setHasCopied(false);
    }, 1500);
    return () => clearTimeout(timeoutId);
  }, [hasCopied]);

  return (
    <TooltipProvider>
      <Tooltip open={hasCopied}>
        <TooltipTrigger asChild>
          <Button
            disableRipple
            size="icon"
            variant={variant}
            className={cn('relative h-6 w-6 hover:bg-transparent', className)}
            onClick={(event) => {
              event.stopPropagation();
              copyToClipboardWithMeta(value, analyticsEvent);
              setHasCopied(true);
            }}
            {...props}
          >
            <span className="sr-only">Copy</span>
            <Copy className={cn('h-3 w-3')} />
          </Button>
        </TooltipTrigger>
        <TooltipContent
          className="bg-white text-text-primary"
          container={container}
        >
          {common('copied-to-clipboard')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default CopyButton;
